import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { walletService } from '@/services/wallet.service';
import { WalletTransaction } from '@/types/wallet.types';

interface WalletSummaryProps {
  balance?: number;
  isLoading?: boolean;
}

/**
 * Format amount in Indian currency format
 */
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
  });
};

const LoadingSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="h-32 bg-gradient-to-br from-primary-400 to-primary-500 rounded-xl mb-4"></div>
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-secondary-200"></div>
          <div className="flex-1">
            <div className="h-4 bg-secondary-200 rounded w-2/3 mb-1"></div>
            <div className="h-3 bg-secondary-100 rounded w-1/3"></div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const TransactionItem: React.FC<{ transaction: WalletTransaction }> = ({ transaction }) => {
  const isCredit = transaction.type === 'CREDIT' || transaction.type === 'REFUND';

  return (
    <div className="flex items-center gap-3 py-2">
      <div
        className={`p-1.5 rounded-full ${
          isCredit ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
        }`}
      >
        {isCredit ? (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
          </svg>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-secondary-700 truncate">{transaction.description}</p>
        <p className="text-xs text-secondary-400">{formatDate(transaction.created_at)}</p>
      </div>
      <span
        className={`text-sm font-medium ${isCredit ? 'text-green-600' : 'text-red-600'}`}
      >
        {isCredit ? '+' : '-'}{formatCurrency(transaction.amount)}
      </span>
    </div>
  );
};

export const WalletSummary: React.FC<WalletSummaryProps> = ({
  balance: propBalance,
  isLoading: propIsLoading,
}) => {
  const navigate = useNavigate();
  const [balance, setBalance] = useState<number>(propBalance ?? 0);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(propIsLoading ?? true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (propBalance !== undefined) {
      setBalance(propBalance);
    }
  }, [propBalance]);

  useEffect(() => {
    if (propIsLoading !== undefined) {
      setIsLoading(propIsLoading);
    }
  }, [propIsLoading]);

  useEffect(() => {
    const fetchWalletData = async () => {
      // Skip if balance is provided via props
      if (propBalance !== undefined) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const [balanceRes, transactionsRes] = await Promise.all([
          walletService.getBalance(),
          walletService.getTransactions({}, 1, 5),
        ]);

        // API interceptor unwraps { success, data } - backend returns { balance: 123 }
        const balanceData = balanceRes.data?.balance ?? balanceRes.data ?? 0;
        setBalance(typeof balanceData === 'number' && !isNaN(balanceData) ? balanceData : 0);

        // API interceptor unwraps { success, data } - backend returns { data: [...], meta: {...} }
        const txData = transactionsRes.data?.data || transactionsRes.data || [];
        setTransactions(Array.isArray(txData) ? txData : []);
      } catch (err) {
        setError('Failed to load wallet data');
        console.error('Error fetching wallet data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWalletData();
  }, [propBalance]);

  const isLowBalance = balance < 50;

  if (isLoading) {
    return (
      <Card className="h-full">
        <LoadingSkeleton />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full">
        <div className="text-center py-8">
          <p className="text-sm text-secondary-500">{error}</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.location.reload()}
            className="mt-2"
          >
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="h-full" padding="none">
      {/* Balance Section */}
      <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-t-xl p-5 text-white">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-primary-100 text-sm font-medium">Wallet Balance</p>
            <p className="mt-1 text-3xl font-bold tracking-tight">
              {formatCurrency(balance)}
            </p>
          </div>
          <div className="p-2 bg-white/10 rounded-lg">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
          </div>
        </div>

        {/* Low Balance Warning */}
        {isLowBalance && (
          <div className="mt-3 flex items-center gap-2 bg-amber-500/20 text-amber-100 px-3 py-2 rounded-lg">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span className="text-xs font-medium">Low balance</span>
          </div>
        )}

        <button
          onClick={() => navigate('/wallet/topup')}
          className="mt-4 w-full flex items-center justify-center gap-2 bg-white text-primary-700 px-4 py-2 rounded-lg font-medium hover:bg-primary-50 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Credits
        </button>
      </div>

      {/* Recent Transactions */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-secondary-700">Recent Transactions</h4>
          <button
            onClick={() => navigate('/wallet')}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            View All
          </button>
        </div>

        {transactions.length === 0 ? (
          <div className="text-center py-6">
            <svg
              className="w-10 h-10 text-secondary-300 mx-auto mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <p className="text-sm text-secondary-500">No transactions yet</p>
          </div>
        ) : (
          <div className="divide-y divide-secondary-100">
            {transactions.map((transaction) => (
              <TransactionItem key={transaction.id} transaction={transaction} />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

export default WalletSummary;
